import React from 'react';
import { AlertTriangle, WifiOff, RefreshCw } from 'lucide-react';

const STATUS = {
  connected: ['#2EF2A5', 'Connected'], online: ['#2EF2A5', 'Online'], up: ['#2EF2A5', 'Up'],
  ok: ['#2EF2A5', 'OK'], running: ['#2EF2A5', 'Running'], healthy: ['#2EF2A5', 'Healthy'],
  contained: ['#2EF2A5', 'Contained'], resolved: ['#2EF2A5', 'Resolved'],
  degraded: ['#FFCC66', 'Degraded'], warning: ['#FFCC66', 'Warning'], investigating: ['#FFCC66', 'Investigating'],
  info: ['#5AF3FF', 'Info'],
  offline: ['#FF5C7A', 'Offline'], down: ['#FF5C7A', 'Down'], critical: ['#FF5C7A', 'Critical'],
  auth_error: ['#FF5C7A', 'Auth error'], high: ['#FF8A5C', 'High'],
  stopped: ['#7E8AA3', 'Stopped'], medium: ['#FFCC66', 'Medium'], low: ['#7E8AA3', 'Low'],
};

export const StatusPill = ({ status = 'connected', label }) => {
  const [c, txt] = STATUS[status] || ['#7E8AA3', status];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ color: c, background: `${c}1a` }} data-testid={`status-${status}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{label || txt}
    </span>
  );
};

export const LiveDot = ({ on = true }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono" style={{ color: on ? '#2EF2A5' : '#FF5C7A' }} data-testid="live-indicator">
    <span className={`h-1.5 w-1.5 rounded-full ${on ? 'animate-blink' : ''}`} style={{ background: on ? '#2EF2A5' : '#FF5C7A' }} />
    {on ? 'LIVE' : 'OFFLINE'}
  </span>
);

export const Panel = ({ title, subtitle, actions, status, children, className = '', testid }) => (
  <div className={`rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/70 backdrop-blur-sm ${className}`} data-testid={testid}>
    {(title || actions || status) && (
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[color:var(--itw-border)]">
        <div className="min-w-0">
          {title && <div className="text-sm font-medium text-[color:var(--itw-text)] truncate">{title}</div>}
          {subtitle && <div className="text-[11px] text-[color:var(--itw-text-3)] font-mono truncate">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">{status && <StatusPill status={status} />}{actions}</div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

export const Kpi = ({ label, value, unit, sub, accent = 'var(--itw-cyan)', testid }) => (
  <div className="rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/70 p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono truncate">{label}</div>
    <div className="mt-1 flex items-baseline gap-1">
      <span className="font-mono text-2xl font-semibold text-[color:var(--itw-text)] tabular-nums">{value}</span>
      {unit && <span className="text-xs text-[color:var(--itw-text-3)]">{unit}</span>}
    </div>
    {sub && <div className="mt-0.5 text-[11px] font-mono truncate" style={{ color: accent }}>{sub}</div>}
  </div>
);

export const Bar = ({ value = 0, max = 100, color = 'var(--itw-cyan)', height = 6 }) => (
  <div className="w-full rounded-full bg-[color:var(--itw-surface-2)] overflow-hidden" style={{ height }}>
    <div className="h-full rounded-full transition-[width] duration-500 ease-out"
      style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
  </div>
);

export const ConnectionState = ({ status = 'offline', source, onRetry }) => (
  <div className="flex flex-col items-center justify-center text-center py-12 px-4" data-testid="connection-state">
    <div className="h-12 w-12 rounded-full grid place-items-center border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)]">
      {status === 'auth_error' ? <AlertTriangle className="h-6 w-6 text-[#FF5C7A]" /> : <WifiOff className="h-6 w-6 text-[#FF5C7A]" />}
    </div>
    <div className="mt-3 text-sm text-[color:var(--itw-text)]">
      {status === 'auth_error' ? 'Authentifizierung fehlgeschlagen' : status === 'degraded' ? 'Dienst eingeschränkt' : 'Verbindung momentan nicht verfügbar'}
    </div>
    <div className="mt-1 text-[11px] text-[color:var(--itw-text-3)] font-mono">
      {source ? `source: ${source}` : 'Keine Live-Daten – es werden keine Platzhalterwerte angezeigt.'}
    </div>
    {onRetry && (
      <button onClick={onRetry} className="mt-4 inline-flex items-center gap-1.5 text-xs text-[color:var(--itw-cyan)] hover:underline" data-testid="connection-retry">
        <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
      </button>
    )}
  </div>
);
